import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CopyIcon } from "@/components/Icons";
import type { WebDictationHistoryItem } from "@/web-dictation-interactions";

// "Recent dictations": the last few results kept on this device, each one a
// tap to copy. Shared by the desktop Dictation panel and the mobile one.
const DictationHistoryList = ({
  history,
  onCopy,
  empty,
}: {
  history: readonly WebDictationHistoryItem[];
  onCopy: (text: string) => Promise<boolean>;
  empty: ReactNode;
}) => {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<{
    at: number;
    copied: boolean;
  }>();

  const latestAt = history[0]?.at;
  useEffect(() => {
    setCopyState(undefined);
  }, [latestAt]);

  const copyDictation = async (item: WebDictationHistoryItem) => {
    const copied = await onCopy(item.text);
    setCopyState({ at: item.at, copied });
  };

  return (
    <section data-testid="dictation-history" className="space-y-3">
      <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">
        {t("desktopDictation.history.title")}
      </p>
      {history.length ? (
        <div className="glass-tile divide-y divide-[var(--hairline)] overflow-hidden rounded-[26px]">
          {history.map((item) => {
            const state = copyState?.at === item.at ? copyState : undefined;
            return (
              <button
                key={item.at}
                type="button"
                data-action-id="dictation.history.copy"
                onClick={() => void copyDictation(item)}
                className="flex w-full items-center gap-4 px-5 py-3.5 text-start transition-colors duration-200 hover:bg-[var(--glass-bg-hover)]"
              >
                <span className="line-clamp-2 min-w-0 flex-1 text-sm text-[var(--text-secondary)]">
                  {item.text}
                </span>
                <span
                  aria-live="polite"
                  className={`flex shrink-0 items-center gap-1.5 text-xs font-semibold ${
                    state && !state.copied
                      ? "text-[var(--destructive)]"
                      : "text-[var(--accent)]"
                  }`}
                >
                  <CopyIcon size={14} />
                  {state?.copied
                    ? t("desktopDictation.recovery.copied")
                    : state
                      ? t("desktopDictation.recovery.failed")
                      : t("desktopDictation.recovery.copy")}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="glass-tile rounded-[26px] px-5 py-4 text-sm text-[var(--text-secondary)]">
          {empty}
        </p>
      )}
      {history.length ? (
        <p className="px-3 text-xs text-[var(--text-muted)]">
          {t("webDictation.history.footer")}
        </p>
      ) : null}
    </section>
  );
};

export default DictationHistoryList;
